// The whole file sits in an IIFE because the loading script tag lives inside
// the main-product section: the theme editor re-executes it on every section
// re-render, and bare top-level const/class bindings would throw "already been
// declared" and silently kill the file the second time around.
(() => {
const h7Track = (eventName, detail = {}) => {
  window.H7Analytics?.track(eventName, detail);
};

const h7SingleLineFitButtons = new WeakSet();

// The fitted size is a pure function of two things: the text in the label and
// the width it has to fit into. Neither the arithmetic nor the result changes
// between two visits to the same label at the same width, but the measuring
// does not come free: a style invalidation, two getComputedStyle reads and up
// to four forced layouts, on the one interaction this page exists for. The
// shopper moves between a small fixed set of labels, one per rung times
// one-time or subscribe, so the answer is remembered against those two inputs
// and replayed without touching layout at all.
//
// Keyed by label element so a detached button's entry goes with it.
const h7SingleLineFitCache = new WeakMap();
// A window resize sweeps a continuous range of widths, and every one of those
// would otherwise be a permanent key. The real working set is a handful.
const h7SingleLineFitCacheLimit = 24;

const h7FitSingleLineButtonLabel = (button) => {
  const label = button.querySelector(':scope > span');
  if (!label || button.clientWidth <= 0) return;

  // Read before the reset below. The button is full width, so its own box does
  // not depend on the label's font size and this measurement is stable.
  const buttonStyle = window.getComputedStyle(button);
  const availableWidth = button.clientWidth
    - Number.parseFloat(buttonStyle.paddingLeft || 0)
    - Number.parseFloat(buttonStyle.paddingRight || 0);
  if (availableWidth <= 0) return;

  let cache = h7SingleLineFitCache.get(label);
  if (!cache) {
    cache = new Map();
    h7SingleLineFitCache.set(label, cache);
  }

  const cacheKey = `${Math.round(availableWidth)}|${label.textContent}`;
  if (cache.has(cacheKey)) {
    // An empty entry is a real answer: this label fits at its natural size and
    // wants no override, which is not the same as never having been measured.
    const fitted = cache.get(cacheKey);
    if (fitted) label.style.setProperty('--h7-primary-cta-font-size', fitted);
    else label.style.removeProperty('--h7-primary-cta-font-size');
    return;
  }

  label.style.removeProperty('--h7-primary-cta-font-size');
  const labelStyle = window.getComputedStyle(label);
  const naturalWidth = label.scrollWidth;
  const naturalFontSize = Number.parseFloat(labelStyle.fontSize || 0);

  if (cache.size >= h7SingleLineFitCacheLimit) cache.clear();

  if (naturalWidth <= availableWidth || naturalFontSize <= 0) {
    cache.set(cacheKey, '');
    return;
  }

  const fittedFontSize = naturalFontSize * (availableWidth / naturalWidth) * 0.98;
  label.style.setProperty('--h7-primary-cta-font-size', `${Math.max(1, fittedFontSize).toFixed(2)}px`);

  // Font shaping is not perfectly linear for every locale. Tighten the fit
  // against the rendered width so long translated labels never clip.
  for (let pass = 0; pass < 3 && label.scrollWidth > availableWidth; pass += 1) {
    const renderedFontSize = Number.parseFloat(window.getComputedStyle(label).fontSize || 0);
    if (renderedFontSize <= 0 || label.scrollWidth <= 0) break;
    const refinedFontSize = renderedFontSize * (availableWidth / label.scrollWidth) * 0.98;
    label.style.setProperty('--h7-primary-cta-font-size', `${Math.max(1, refinedFontSize).toFixed(2)}px`);
  }

  cache.set(cacheKey, label.style.getPropertyValue('--h7-primary-cta-font-size'));
};

const h7InitSingleLineFitButton = (button) => {
  if (h7SingleLineFitButtons.has(button)) return;
  const label = button.querySelector(':scope > span');
  if (!label) return;

  h7SingleLineFitButtons.add(button);
  let fitFrame = 0;
  const scheduleFit = () => {
    window.cancelAnimationFrame(fitFrame);
    fitFrame = window.requestAnimationFrame(() => h7FitSingleLineButtonLabel(button));
  };

  if ('ResizeObserver' in window) {
    const resizeObserver = new ResizeObserver(scheduleFit);
    resizeObserver.observe(button);
  }

  const labelObserver = new MutationObserver(scheduleFit);
  labelObserver.observe(label, { childList: true, characterData: true, subtree: true });
  document.fonts?.ready.then(scheduleFit);
  scheduleFit();
};

const h7InitSingleLineFitButtons = (root = document) => {
  root.querySelectorAll('[data-h7-single-line-fit]').forEach(h7InitSingleLineFitButton);
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => h7InitSingleLineFitButtons(), { once: true });
} else {
  h7InitSingleLineFitButtons();
}

document.addEventListener('shopify:section:load', (event) => h7InitSingleLineFitButtons(event.target));

// h7-product-countdown and the h7Sale* cookie machinery that drove it were
// removed on 10 Aug 2026: roughly 240 lines defining a custom element that no
// section, snippet or template output. tests/storefront.spec.js already
// asserted the PDP carries none of them, and tests/purchase-option-layout.spec.js
// already asserted main-product.liquid does not name one, so the absence was a
// deliberate, tested state and only the implementation was still being parsed
// and executed on every product page render.
//
// It was a Summer Sale deadline clock with a cookie-persisted "Need more time"
// extension, keyed on a per-day campaign. Nothing else in this file referenced
// any of it. Recover it from git history if the offer clock is ever wanted
// back; the two fixture tests that exercised it came out in the same commit.

if (!customElements.get('h7-product-gallery')) {
  customElements.define(
    'h7-product-gallery',
    class H7ProductGallery extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;
        // Every listener this element adds hangs off one controller, so
        // disconnectedCallback can drop the lot in a line. This was the only
        // element in the file that added listeners it never removed and never
        // cleared its own initialized flag, so a theme-editor block move that
        // re-attaches the same node left the old handlers bound to a main
        // image that is no longer in the document.
        this.listeners = new AbortController();
        this.buttons = Array.from(this.querySelectorAll('[data-h7-gallery-thumb]'));
        this.status = this.querySelector('[data-h7-gallery-status]');
        this.mainImage = this.closest('.product__media-wrapper')?.querySelector('.product__media img');
        if (!this.mainImage) {
          // No media on the product means no main image to swap into, and the
          // strip is rendered unconditionally by the section. Left alone it is
          // six buttons that look pressable and do nothing, which is worse than
          // no strip at all.
          this.hidden = true;
          return;
        }

        const selectedButton = this.buttons.find((button) => button.classList.contains('is-active'));
        if (selectedButton?.dataset.alt) this.mainImage.alt = selectedButton.dataset.alt;
        this.mainImage.dataset.h7FeaturedCrop = 'true';

        this.buttons.forEach((button, index) => {
          button.addEventListener('click', () => this.select(button, index), { signal: this.listeners.signal });
          button.addEventListener('keydown', (event) => this.onKeydown(event, index), {
            signal: this.listeners.signal,
          });
        });

        // Six thumbnails were six Tab stops sitting between the product title
        // and the buy box, which is a lot of keyboard to get past on the way to
        // the thing the page is for. The strip is one control now: Tab reaches
        // whichever shot is showing, the arrow keys move along the row, and Tab
        // again leaves. Applied here rather than in the markup so a visitor
        // with no JavaScript keeps all six reachable, which is the only state
        // in which they would be, since nothing else about this strip works
        // without it.
        this.syncRovingFocus();
        this.wireArrows();
      }

      /*
       * Arrow keys select as they move, rather than moving focus and waiting
       * for a press. Selecting only swaps which picture is on screen, so there
       * is nothing to confirm and nothing to undo, and the arrows over the
       * image already behave this way. Home and End are the ends of the strip.
       *
       * Both axes are accepted. The strip is a row on a wide screen and the
       * six of them can wrap on a narrow one, so insisting on left and right
       * would leave a visitor pressing down against a control that looks
       * stacked.
       */
      onKeydown(event, index) {
        if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;

        let target = null;
        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') target = index + 1;
        else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') target = index - 1;
        else if (event.key === 'Home') target = 0;
        else if (event.key === 'End') target = this.buttons.length - 1;
        if (target === null || this.buttons.length === 0) return;

        // Wraps, so the row has no dead end at either edge. step() does the
        // same for the arrows over the image.
        const next = (target + this.buttons.length) % this.buttons.length;
        // Stops the page scrolling under the strip on an arrow press.
        event.preventDefault();
        this.select(this.buttons[next], next);
        this.buttons[next].focus();
      }

      /*
       * One Tab stop for the whole strip, on whichever thumbnail is currently
       * showing. Called on connect and after every selection, including the
       * ones made by the arrows over the image, so the Tab stop follows the
       * picture rather than staying where the visitor last left it.
       */
      syncRovingFocus() {
        if (this.buttons.length < 2) return;
        const activeIndex = Math.max(
          0,
          this.buttons.findIndex((button) => button.classList.contains('is-active'))
        );
        this.buttons.forEach((button, index) => {
          button.tabIndex = index === activeIndex ? 0 : -1;
        });
      }

      disconnectedCallback() {
        // The fade timer holds the element and the main image alive for its
        // 90ms; a section re-render inside that window used to leave it writing
        // into a subtree that is no longer on the page.
        window.clearTimeout(this.swapTimer);
        this.swapTimer = 0;
        this.listeners?.abort();
        // Everything above was torn down, so connectedCallback has to be
        // allowed to rebuild it if the editor re-attaches the element. Same
        // reasoning as h7-purchase-sticky and h7-product-magnifier below.
        this.initialized = false;
      }

      // The arrows sit over the image itself, so they only earn their place
      // once there is more than one shot to step through.
      wireArrows() {
        const wrapper = this.closest('.product__media-wrapper');
        const arrowGroup = wrapper?.querySelector('[data-h7-gallery-arrows]');
        if (!arrowGroup || this.buttons.length < 2) return;

        arrowGroup.querySelectorAll('[data-h7-gallery-arrow]').forEach((arrow) => {
          arrow.addEventListener('click', () => this.step(arrow.dataset.h7GalleryArrow === 'prev' ? -1 : 1), {
            signal: this.listeners.signal,
          });
        });
        arrowGroup.hidden = false;
      }

      step(delta) {
        const current = this.buttons.findIndex((button) => button.classList.contains('is-active'));
        const next = (Math.max(current, 0) + delta + this.buttons.length) % this.buttons.length;
        this.select(this.buttons[next], next);
      }

      select(button, index) {
        const src = button.dataset.src;
        const srcset = button.dataset.srcset;
        const sizes = button.dataset.sizes;
        if (!src || !this.mainImage) return;

        this.buttons.forEach((item) => {
          const active = item === button;
          item.classList.toggle('is-active', active);
          item.setAttribute('aria-pressed', String(active));
        });
        this.syncRovingFocus();

        // Announced from the thumbnail's own alt text, which the section has
        // already translated, so the swap confirms itself without adding a
        // catalogue string. The buy box's offer panel does the same thing for
        // the same reason: everything about this control is visual otherwise.
        if (this.status) this.status.textContent = button.dataset.alt || '';

        // One swap in flight at a time. Stepping through the strip faster than
        // the 90ms fade left two timers racing on the same <img>, and a timer
        // outliving a theme-editor re-render wrote into a detached node.
        window.clearTimeout(this.swapTimer);
        this.mainImage.style.opacity = '0.15';
        this.swapTimer = window.setTimeout(() => {
          this.swapTimer = 0;
          if (srcset) this.mainImage.srcset = srcset;
          else this.mainImage.removeAttribute('srcset');
          if (sizes) this.mainImage.sizes = sizes;
          else this.mainImage.removeAttribute('sizes');
          this.mainImage.src = src;
          this.mainImage.alt = button.dataset.alt || '';
          this.mainImage.dataset.h7FeaturedCrop = String(index === 0);
          this.mainImage.style.opacity = '1';
        }, 90);

        h7Track('gallery_interaction', {
          media_position: index + 1,
          media_alt: button.dataset.alt || '',
        });
      }
    }
  );
}

// The widest copy the gallery already ships, so opening the zoom asks the CDN
// for something it has served before rather than minting a cold rendition.
//
// currentSrc is the wrong answer on its own, twice over. The hero carries a
// separate 800px mobile asset in its srcset, so on a phone currentSrc is that
// crop, and opening a zoom on the small copy is the one thing this feature
// exists to avoid. Shopify renditions instead carry their width in the query
// string, and the srcset hands a phone a narrow one; a nutrition panel at 493px
// is unreadable however far it is scaled. So: widest srcset candidate first,
// then the width parameter raised to the size the gallery already requests.
const h7ZoomSource = (image) => {
  const widest = (image.getAttribute('srcset') || '')
    .split(',')
    .map((candidate) => candidate.trim().split(/\s+/))
    .filter(([url, descriptor]) => url && /^\d+w$/.test(descriptor || ''))
    .sort((a, b) => parseInt(b[1], 10) - parseInt(a[1], 10))[0];

  return (widest ? widest[0] : image.currentSrc || image.src || '').replace(/([?&]width=)\d+/, '$11946');
};

// Enough to read a nutrition panel, not so much that the picture is lost.
// Named because the pan has to do arithmetic with it.
const H7_ZOOM_SCALE = 2.4;

// The origin walks the picture from edge to edge and stops there.
const h7ClampOrigin = (value) => Math.min(100, Math.max(0, value));

// One overlay for the page, built the first time an image is opened.
//
// Dawn's own product media modal cannot do this job here. It renders
// product.media, and five of the six shots in this gallery are theme assets the
// thumbnail strip swaps into the same <img>: the comparison widgets, the ninety
// nights, the ingredients, the magnesium table and the nutrition panel are not
// Shopify media at all. So the only correct source for a zoom is whatever that
// <img> is showing at the moment it is pressed.
const h7MediaLightbox = {
  element: null,
  image: null,
  opener: null,
  zoomed: false,
  origin: { x: 50, y: 50 },

  build(labels) {
    if (!this.element) {
      // The theme editor re-runs this whole file on every section render, and
      // an overlay left by the previous run still carries that run's listeners,
      // closed over that run's lightbox object. Two owners of one node is how
      // the zoom state and the DOM drift apart, so the old one is discarded
      // rather than adopted. this.element is per-run, so this branch is taken
      // exactly once after each re-render.
      // Discarding an open overlay takes its close() with it, and close() is
      // what gives the page its scroll back. A re-render while the zoom was
      // open therefore left <body> locked with no overlay on screen and no
      // control anywhere that could unlock it: the visitor is looking at the
      // buy box and the page will not move. Released here rather than trusted
      // to the next close, because there may not be one.
      document.getElementById('H7MediaLightbox')?.remove();
      document.body.classList.remove('overflow-hidden');

      this.element = document.createElement('div');
      this.element.id = 'H7MediaLightbox';
      this.element.className = 'h7-media-lightbox';
      this.element.setAttribute('role', 'dialog');
      this.element.setAttribute('aria-modal', 'true');
      this.element.hidden = true;
      this.element.innerHTML = [
        '<button type="button" class="h7-media-lightbox__close" data-h7-lightbox-close>',
        '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">',
        '<path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>',
        '</svg><span class="visually-hidden"></span></button>',
        '<div class="h7-media-lightbox__viewport" data-h7-lightbox-viewport>',
        // draggable="false" because an image is draggable by default and the
        // browser takes the pointer away to carry it: a press and a pull sent
        // one pointermove and then pointercancel, so the picture lurched a few
        // pixels, stopped dead, and left a ghost of itself under the cursor.
        // Panning is what this overlay is for, so the native drag has to go.
        '<img class="h7-media-lightbox__image" alt="" draggable="false" data-h7-lightbox-image>',
        '</div>',
      ].join('');
      document.body.appendChild(this.element);
      this.image = this.element.querySelector('[data-h7-lightbox-image]');
      this.wire();
    }

    // Re-applied on every open: the labels come off whichever magnifier was
    // pressed, and a section re-render can hand over a newly translated pair.
    const close = this.element.querySelector('[data-h7-lightbox-close]');
    this.element.setAttribute('aria-label', labels.dialog);
    close.setAttribute('aria-label', labels.close);
    close.querySelector('.visually-hidden').textContent = labels.close;
    return this.element;
  },

  wire() {
    const viewport = this.element.querySelector('[data-h7-lightbox-viewport]');
    // Every listener this run adds hangs off one controller, including the
    // document-level Escape below. Without it, each theme-editor re-render
    // left another Escape handler bound to the document, closed over a
    // lightbox object whose element had already been discarded and replaced.
    // Same reasoning as the AbortController on h7-product-gallery.
    this.listeners?.abort();
    this.listeners = new AbortController();
    const { signal } = this.listeners;

    this.element.querySelector('[data-h7-lightbox-close]').addEventListener('click', () => this.close(), { signal });

    // A drag ends in a click the browser fires anyway, and reading that click
    // as a press would zoom straight back out the moment a pan let go. It is
    // spent by the next press rather than by a one-shot listener on the click
    // itself: a touch drag frequently ends with no click at all, and the
    // listener left armed then ate the tap after it, so the first press that
    // followed a pan did nothing at all.
    let dragging = null;
    let dragged = false;
    viewport.addEventListener('pointerdown', () => { dragged = false; }, { signal, capture: true });

    // Pressing the backdrop closes; pressing the image does not, because the
    // image is the thing being looked at and it carries the zoom toggle.
    viewport.addEventListener('click', (event) => {
      if (dragged) return;
      if (event.target === this.image) this.toggleZoom(event);
      else this.close();
    }, { signal });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !this.element.hidden) this.close();
    }, { signal });

    // Dragging pans the zoomed image. Pointer capture rather than document
    // listeners so a drag that leaves the viewport still ends cleanly.
    this.image.addEventListener('pointerdown', (event) => {
      if (!this.zoomed) return;
      dragging = { x: event.clientX, y: event.clientY, origin: { ...this.origin } };
      this.image.setPointerCapture(event.pointerId);
    }, { signal });
    this.image.addEventListener('pointermove', (event) => {
      if (!dragging) return;
      const rect = this.image.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      const moveX = event.clientX - dragging.x;
      const moveY = event.clientY - dragging.y;
      // A finger never holds still, so a press that wanders a pixel or two is
      // still a press. Only past this is the click at the end of it a pan
      // rather than the tap that fits the picture back in the frame.
      if (Math.abs(moveX) + Math.abs(moveY) > 4) dragged = true;
      // Moving the origin does not move the picture by the same share of its
      // width: it moves it by that share of the width the scale added, which is
      // (scale - 1) of the unscaled box. rect is the scaled box, so the pointer
      // travel that walks the picture end to end is rect over the scale, times
      // scale - 1. Divided by the scaled width instead, the picture trailed the
      // finger by two fifths of every drag and never quite went where it was
      // put.
      const travelX = (rect.width / H7_ZOOM_SCALE) * (H7_ZOOM_SCALE - 1);
      const travelY = (rect.height / H7_ZOOM_SCALE) * (H7_ZOOM_SCALE - 1);
      this.origin = {
        x: h7ClampOrigin(dragging.origin.x - (moveX / travelX) * 100),
        y: h7ClampOrigin(dragging.origin.y - (moveY / travelY) * 100),
      };
      this.applyZoom();
    }, { signal });
    const endDrag = (event) => {
      if (!dragging) return;
      dragging = null;
      if (this.image.hasPointerCapture?.(event.pointerId)) this.image.releasePointerCapture(event.pointerId);
    };
    this.image.addEventListener('pointerup', endDrag, { signal });
    this.image.addEventListener('pointercancel', endDrag, { signal });
  },

  open(source, alt, opener, labels) {
    this.build(labels);
    this.opener = opener;
    this.zoomed = false;
    this.origin = { x: 50, y: 50 };
    this.image.src = source;
    this.image.alt = alt || '';
    this.applyZoom();
    this.element.hidden = false;
    document.body.classList.add('overflow-hidden');
    const close = this.element.querySelector('[data-h7-lightbox-close]');
    if (typeof trapFocus === 'function') trapFocus(this.element, close);
    else close.focus();
  },

  close() {
    if (!this.element || this.element.hidden) return;
    this.element.hidden = true;
    this.zoomed = false;
    this.element.classList.remove('is-zoomed');
    document.body.classList.remove('overflow-hidden');
    if (typeof removeTrapFocus === 'function') removeTrapFocus(this.opener);
    else this.opener?.focus();
    this.opener = null;
  },

  // Tap to zoom, tap again to fit. Two states rather than a slider: the point
  // is reading a label, and one press either way is the whole interaction.
  toggleZoom(event) {
    this.zoomed = !this.zoomed;
    if (this.zoomed) {
      const rect = this.image.getBoundingClientRect();
      this.origin = {
        x: rect.width ? h7ClampOrigin(((event.clientX - rect.left) / rect.width) * 100) : 50,
        y: rect.height ? h7ClampOrigin(((event.clientY - rect.top) / rect.height) * 100) : 50,
      };
    } else {
      this.origin = { x: 50, y: 50 };
    }
    this.applyZoom();
  },

  applyZoom() {
    this.element.classList.toggle('is-zoomed', this.zoomed);
    this.image.style.transformOrigin = `${this.origin.x}% ${this.origin.y}%`;
    this.image.style.transform = this.zoomed ? `scale(${H7_ZOOM_SCALE})` : 'scale(1)';
  },
};

if (!customElements.get('h7-product-magnifier')) {
  customElements.define(
    'h7-product-magnifier',
    class H7ProductMagnifier extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;
        this.listeners = new AbortController();

        const toggle = this.querySelector('[data-h7-media-zoom]');
        // Read at press time, never cached: h7-product-gallery swaps the source
        // of this one <img> as the thumbnail strip is walked, so the element is
        // stable and the picture behind it is not.
        const image = this.querySelector('.product__media img');
        if (!toggle || !image) return;

        toggle.addEventListener(
          'click',
          () => {
            h7MediaLightbox.open(h7ZoomSource(image), image.alt, toggle, {
              dialog: this.dataset.h7ZoomLabel || '',
              close: this.dataset.h7ZoomClose || '',
            });
            h7Track('media_zoom', { media_alt: image.alt || '' });
          },
          { signal: this.listeners.signal }
        );
      }

      disconnectedCallback() {
        this.listeners?.abort();
        // Everything above was torn down, so connectedCallback has to be
        // allowed to rebuild it if the editor re-attaches the element. Same
        // reasoning as h7-product-gallery above.
        this.initialized = false;
      }
    }
  );
}

if (!customElements.get('h7-product-offer')) {
  customElements.define(
    'h7-product-offer',
    class H7ProductOffer extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;
        this.offerListeners = new AbortController();
        const listenerOptions = { signal: this.offerListeners.signal };
        // The subscription toggle and the delivery bar render below the add to
        // basket button, outside this element, so lookups fall back to the
        // product info column before giving up.
        this.scope = this.closest('.product__info-container') || document;
        const q = (selector) => this.querySelector(selector) || this.scope.querySelector(selector);
        const qa = (selector) => {
          const inside = Array.from(this.querySelectorAll(selector));
          return inside.length ? inside : Array.from(this.scope.querySelectorAll(selector));
        };
        this.buttons = Array.from(this.querySelectorAll('[data-h7-bundle]'));
        this.purchaseButtons = qa('[data-h7-purchase-option]');
        this.quantityInput = this.querySelector('[data-h7-bundle-quantity]');
        this.statusRegion = this.querySelector('[data-h7-offer-status]');
        this.sellingPlanInput = this.querySelector('[data-h7-selling-plan]');
        this.pouchCountInput = this.querySelector('[data-h7-cart-pouch-count]');
        this.savingInput = this.querySelector('[data-h7-cart-saving]');
        this.includedGiftsInput = this.querySelector('[data-h7-included-gifts]');
        this.checkoutGiftValueInput = this.querySelector('[data-h7-checkout-gift-value]');
        this.giftClaimInput = q('[data-h7-gift-claim]');
        this.subscriberGifts = q('[data-h7-subscriber-gifts]');
        this.giftLadder = q('[data-h7-gift-list]');
        this.giftStatusTitle = q('[data-h7-gift-status-title]');
        this.giftStatusDetail = q('[data-h7-gift-status-detail]');
        this.giftUnlockButton = q('[data-h7-gift-unlock]');
        this.productForm = this.quantityInput?.getAttribute('form')
          ? document.getElementById(this.quantityInput.getAttribute('form'))
          : null;
        this.variantInput = this.productForm?.querySelector('.product-variant-id');
        this.primaryCta = this.productForm?.querySelector('[data-h7-primary-cta]');
        this.primaryCtaText = this.primaryCta?.querySelector('span') || this.primaryCta;
        this.baseVariantId = this.variantInput?.value || '';
        // These four have no element in any section or snippet: the buy choice
        // panel below the ladder took over the total, the strike and the
        // percentage, and the per-pouch line went with the rung subline. They
        // are NOT dead, and were nearly deleted as such on 10 Aug 2026.
        //
        // tests/product-state-fixtures.spec.js renders its own buy box carrying
        // all four and asserts twenty-five figures through them: the bundle
        // total per rung, the struck reference and its show and hide rule, and
        // the per-pouch division. That is the suite's only direct handle on the
        // ladder's arithmetic, and this store has a history of the page and the
        // cart disagreeing about exactly these numbers. Removing the writers
        // removes the test, not the feature.
        //
        // Keep them. If the elements are ever wanted on the storefront again,
        // the contract is already here and already covered.
        this.total = this.querySelector('[data-h7-bundle-total]');
        this.regularTotal = this.querySelector('[data-h7-regular-total]');
        this.saving = this.querySelector('[data-h7-saving]');
        this.perServing = q('[data-h7-bundle-per-serving]');
        this.perPouch = q('[data-h7-per-pouch]');
        // [data-h7-subscription-saving] is dead in the same way: only
        // [data-h7-subscription-saving-label] is rendered, and an attribute
        // selector does not match a longer attribute name, so the read below
        // it is the only one that ever resolved.
        this.subscriptionSavingLabel = q('[data-h7-subscription-saving-label]');
        this.oneTimeComparePrice = q('[data-h7-one-time-compare-price]');
        this.oneTimeOptionPrice = q('[data-h7-one-time-option-price]');
        this.subscriptionComparePrice = q('[data-h7-subscription-compare-price]');
        this.subscriptionOptionPrice = q('[data-h7-subscription-option-price]');
        this.subscriptionPolicyInput = q('[data-h7-subscription-policy]');
        this.oneTimeHeading = q('[data-h7-one-time-heading]');
        this.subscriptionHeading = q('[data-h7-subscription-heading]');
        this.subscriptionContents = q('[data-h7-subscription-contents]');
        this.subscriptionButton = q('[data-h7-purchase-option="subscription"]');
        this.subscriptionOption = q('[data-h7-subscription-option]');
        this.subscriptionOptionDetail = this.subscriptionButton?.querySelector('[data-h7-subscription-detail]');
        this.subscriptionFrequencyWrap = q('[data-h7-subscription-frequency-wrap]');
        this.subscriptionFrequency = q('[data-h7-subscription-frequency]');
        this.purchaseGroup = q('[data-h7-purchase-group]');
        this.subscriptionPlans = qa('[data-h7-subscription-plan]').map((plan) => ({
          pouches: Math.max(1, Number(plan.dataset.h7PlanPouches || 1)),
          id: plan.dataset.h7PlanId || '',
          variantId: plan.dataset.h7PlanVariantId || '',
          total: Math.max(0, Number(plan.dataset.h7PlanTotal || 0)),
          renewalTotal: Math.max(0, Number(plan.dataset.h7PlanRenewalTotal || 0)),
          name: plan.dataset.h7PlanName || '',
        }));
        this.subscriptionPlanSelections = new Map();
        this.perServingLabel = this.dataset.pricePerNightLabel || 'per night';
        this.perPouchLabel = this.dataset.perPouchLabel || 'per pouch';
        this.pouchSingular = this.dataset.pouchSingular || 'pouch';
        this.pouchPlural = this.dataset.pouchPlural || 'pouches';
        this.subscriptionContentsTemplate = this.dataset.subscriptionContentsTemplate || '{count} {pouch} every delivery';
        // Keyed by pouch count. A count with no sentence of its own falls
        // through to the numeric template above.
        this.subscriptionOptionLabel = this.dataset.subscriptionOptionLabel || 'subscribe & save';
        this.oneTimeOptionLabel = this.dataset.oneTimeOptionLabel || 'one-time purchase';
        this.subscriptionHeadingLabel = this.dataset.subscriptionHeadingLabel || 'Subscribe and save';
        this.oneTimeHeadingLabel = this.dataset.oneTimeHeadingLabel || 'One-time purchase';
        this.fourMonthClubLabel = this.dataset.fourMonthClubLabel || '4-month club plan';
        this.fourMonthSupplyLabel = this.dataset.fourMonthSupplyLabel || '4-month supply';
        // The frequency dropdown builds its option text at runtime, so its
        // wording cannot be written into the markup the way every other label
        // here is. These four come off the element as translated strings and
        // the English literals stay only as the last-resort fallback, which is
        // what the rest of this file already does.
        this.frequencyMonthlyLabel = this.dataset.frequencyMonthlyLabel || 'Every month';
        this.frequencyMonthsTemplate = this.dataset.frequencyMonthsTemplate || 'Every {count} months';
        this.subscriptionUnavailableLabel = this.dataset.subscriptionUnavailableLabel || 'Unavailable';
        this.subscriptionPendingLabel =
          this.dataset.subscriptionPendingLabel || 'Subscriptions are not available yet.';
        // Subscribe is the opening choice, matching what the server paints.
        // Starting on one-time here made the panel flip after hydration.
        //
        // It was one-time for part of 9 August 2026, while the checkout's
        // "Recurring subtotal" line still quoted the raw selling plan price and
        // so announced a renewal the plan never took. That is fixed: the
        // discount lives in the plan now, and the recurring line agrees with
        // the charge on every rung. There is no longer anything to hide from.
        //
        // updateAvailability falls back to one-time when no plan is sellable,
        // which is what puts the single pouch rung on one-time.
        this.selectedPurchaseOption = 'subscription';
        this.unitPrice = Math.max(0, Number(this.dataset.unitPrice || 0));
        // Absent attribute means the sale is off, which must read as "no
        // reference price" rather than as 0% off.
        const salePercent = Number(this.dataset.salePercent);
        this.salePercent = Number.isFinite(salePercent) && salePercent > 0 && salePercent < 100 ? salePercent : 0;
        this.currency = window.Shopify?.currency?.active || this.dataset.currency || 'GBP';
        this.formatter = new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
          style: 'currency',
          currency: this.currency,
        });
        this.buttons.forEach((button) => {
          // Native radios fire change on click and on arrow-key movement, so a
          // single listener covers pointer and keyboard alike.
          const eventName = button.tagName === 'INPUT' ? 'change' : 'click';
          button.addEventListener(eventName, () => this.selectBundle(button, true), listenerOptions);
        });
        this.purchaseButtons.forEach((button) => {
          button.addEventListener('click', () => this.selectPurchaseOption(button.dataset.h7PurchaseOption, true), listenerOptions);
        });
        // The subscribe card is taller than the button inside it: the plan terms
        // and the frequency select sit below, and pressing that part of the
        // card did nothing. Anything in the card that is not already a control
        // now selects the option, which is what a shopper aiming at the card
        // expects. Guarded so the frequency dropdown still opens on its own.
        this.subscriptionOption?.addEventListener('click', (event) => {
          if (event.target.closest('select, button, a, input, label')) return;
          this.selectPurchaseOption('subscription', true);
        }, listenerOptions);

        this.giftUnlockButton?.addEventListener('click', () => {
          // The button unlocks the pack, so it lands on a rung that can carry a
          // plan. Selecting the rung is what carries it, because the single
          // pouch publishes none and switching purchase option alone would
          // leave the pack locked on that rung.
          const startingPouches = this.state?.pouches;
          // The rung the shopper is already on keeps the pack whenever it can
          // sell a plan, which since 10 August 2026 is the rung the page opens
          // on. Moving them to two pouches from there would answer "unlock the
          // gifts" by quietly halving the order.
          const heldRungKeepsPlan = Boolean(this.state?.subscriptionPlanId && this.state?.subscriptionTotal > 0);
          // A sold out rung renders disabled, and moving the shopper onto one
          // would be worse than leaving them where they are.
          const twoPouch = heldRungKeepsPlan
            ? null
            : this.buttons.find(
                (button) =>
                  !button.disabled &&
                  Number(button.dataset.h7BundlePouches ?? button.dataset.h7Bundle ?? 0) === 2
              );
          const movedRung = Boolean(twoPouch && startingPouches !== 2);
          if (movedRung) this.selectBundle(twoPouch, true);
          this.selectPurchaseOption('subscription', true);

          // The switch is only half the decision when it also moved the rung.
          // The gift panel sits well below the ladder, so a visitor who
          // unlocks the pack from down here has the offer applied above them
          // with no sight of it. They are taken back up the page with the rung
          // and the plan already selected, rather than being left at a panel
          // that just changed something out of view.
          const ladder = movedRung ? q('[data-h7-ladder]') : null;
          if (ladder) {
            // The same landing every CTA on the page uses: #h7-shop at the top
            // of the viewport, which opens on the review stars and the title.
            // Centring the ladder instead put the shopper in the middle of the
            // buy box with the page's opening claim scrolled off above them.
            const target =
              document.getElementById('h7-shop') || ladder.closest('.h7-pdp-offer__group--ladder') || ladder;
            const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
            target.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
            // The caret follows the viewport, or the next Tab resumes at the
            // gift panel the visitor has just been scrolled away from.
            const selectedRung = ladder.querySelector('[data-h7-bundle-pouches]:checked') || ladder.querySelector('[data-h7-bundle-pouches]');
            selectedRung?.focus({ preventScroll: true });
            return;
          }

          this.subscriptionButton?.focus({ preventScroll: true });
        }, listenerOptions);
        this.giftClaimInput?.addEventListener('change', () => {
          const isSubscription = this.selectedPurchaseOption === 'subscription' && Boolean(this.state?.subscriptionPlanId);
          this.setIncludedGifts(isSubscription);
        }, listenerOptions);
        this.subscriptionFrequency?.addEventListener('change', () => {
          const selectedPlan = this.subscriptionPlans.find((plan) => plan.id === this.subscriptionFrequency.value);
          if (!selectedPlan || !this.state) return;
          this.selectedPurchaseOption = 'subscription';
          this.subscriptionPlanSelections.set(this.state.pouches, selectedPlan.id);
          this.applySubscriptionPlan(selectedPlan);
          this.updateDisplay();
          h7Track('subscription_frequency_change', {
            pouch_count: this.state.pouches,
            selling_plan_id: selectedPlan.id,
            frequency: this.subscriptionFrequencyLabel(selectedPlan.name),
          });
        }, listenerOptions);

        if (this.dataset.servingsDefaulted === 'true') {
          // Read the handle out first. Falling back to a quoted string
          // directly off a dataset read is how the theme declares a
          // customer-facing default label, and the translation audit treats
          // every one of those as copy needing a catalogue entry. This string
          // is for the console only.
          const warnedHandle = this.dataset.productHandle;
          console.warn(
            `[Hour Seven] product.metafields.custom.servings_per_pouch is not set on "${warnedHandle || 'this product'}". ` +
              'Per-night pricing is falling back to 30 servings a pouch. Set the metafield in Shopify admin.'
          );
        }

        // A ladder with no sellable plan anywhere is the store's worst silent
        // failure, so it is no longer silent.
        //
        // Each multi-pouch rung publishes a plan only when an allocation on its
        // own variant matches that rung's stated price target. That match is
        // deliberate: a rung whose plan is mispriced in admin offers no plan at
        // all rather than quoting one figure and charging another. But the same
        // guard turns a one-penny typo in a Shopify selling plan into a
        // storefront that quietly stops selling subscriptions altogether, and
        // nothing on the page says so, because a missing subscribe card looks
        // exactly like a rung that never had one.
        //
        // The single pouch is excluded: it publishes no plan on purpose.
        if (this.buttons.length && this.subscriptionPlans.length === 0) {
          const warnedHandle = this.dataset.productHandle;
          console.warn(
            `[Hour Seven] no selling plan reached the buy box on "${warnedHandle || 'this product'}". ` +
              'Every rung will sell one-time only. Check that the two and three pouch selling plan ' +
              'prices in Shopify admin still match the targets in snippets/h7-ladder-facts.liquid.'
          );
        }

        this.selectDefaultBundle();
      }

      disconnectedCallback() {
        this.offerListeners?.abort();
        this.initialized = false;
      }

      money(value) {
        return this.formatter.format(Math.max(0, value) / 100);
      }

      resetBundleSelection() {
        this.state = null;
        this.buttons.forEach((button) => {
          button.classList.remove('is-selected');
          if (button.tagName === 'INPUT') button.checked = false;
          else button.setAttribute('aria-pressed', 'false');
          button.closest('[data-h7-ladder-row]')?.classList.remove('is-selected');
        });
        this.purchaseButtons.forEach((button) => {
          button.classList.remove('is-selected');
          button.setAttribute('aria-pressed', 'false');
        });
        this.subscriptionOption?.classList.remove('is-selected');
        if (this.purchaseGroup) this.purchaseGroup.hidden = true;
        if (this.sellingPlanInput) {
          this.sellingPlanInput.value = '';
          this.sellingPlanInput.disabled = true;
        }
        // One-time starts without an Included property. It is enabled only
        // when the shopper actively claims the ebook.
        this.setIncludedGifts(false);
        if (this.checkoutGiftValueInput) this.checkoutGiftValueInput.disabled = true;
        this.updateSubscriberGifts('teaser');
        if (this.primaryCta) {
          this.primaryCta.disabled = true;
          this.primaryCta.setAttribute('aria-disabled', 'true');
        }
        if (this.primaryCtaText) {
          const chooseBundleLabel = (this.dataset.chooseBundleLabel || '').replace(/:\s*$/, '').trim();
          this.primaryCtaText.textContent = chooseBundleLabel ? `${chooseBundleLabel} ↑` : '↑';
        }
      }

      defaultBundleButton() {
        const selectableButtons = this.buttons.filter((button) => (
          !button.disabled &&
          button.getAttribute('aria-disabled') !== 'true'
        ));
        if (selectableButtons.length === 0) return null;
        // Honour the row the server already checked, so the JS default and the
        // no-JS default can never disagree.
        const preChecked = selectableButtons.find((button) => button.defaultChecked);
        if (preChecked) return preChecked;
        // Otherwise the highest-value one-time rung. Subscription is a separate
        // toggle and is never a row here.
        return selectableButtons.reduce((best, button) => {
          const total = Number(button.dataset.h7BundleCartTotal || 0);
          const bestTotal = Number(best.dataset.h7BundleCartTotal || 0);
          return total > bestTotal ? button : best;
        }, selectableButtons[0]);
      }

      selectDefaultBundle() {
        // Always the server's default rung. This used to restore whatever the
        // visitor picked last, which meant a returning shopper never opened on
        // the recommended three-pouch row.
        const defaultButton = this.defaultBundleButton();
        if (!defaultButton) {
          this.resetBundleSelection();
          return;
        }
        // false: opening on a default is not a change the visitor just made,
        // so it stays out of the live region and out of analytics.
        this.selectBundle(defaultButton, false);
      }

      revealPurchaseOptions() {
        if (this.purchaseGroup?.hidden) {
          this.purchaseGroup.hidden = false;
          this.purchaseGroup.classList.add('is-revealing');
          this.purchaseGroup.addEventListener('animationend', () => {
            this.purchaseGroup?.classList.remove('is-revealing');
          }, { once: true });
        }
        // Enabled outright, because reaching here means a selectable rung is
        // selected. This used to restore a snapshot of the button's disabled
        // state taken at connect, and that snapshot is a different question
        // from the one being asked.
        //
        // buy-buttons.liquid disables the submit when
        // selected_or_first_available_variant is unavailable, and that variant
        // follows ?variant= in the URL. So an old email, ad or Shopping feed
        // link pointing at a rung whose bucket has emptied rendered the button
        // disabled, and restoring the snapshot kept it disabled after the JS
        // had moved the shopper onto a rung that was in stock. The ladder said
        // buyable, the price said buyable, and the button did nothing, with no
        // error to explain it. The rung buckets cannot lend to each other, so
        // an empty single pouch bucket beside a full two pouch one is routine
        // rather than exotic.
        //
        // The genuinely-unbuyable case is not handled here and does not need
        // to be: every rung renders disabled when its own variant is
        // unavailable, defaultBundleButton then finds nothing selectable, and
        // resetBundleSelection disables the button and says "Pick your bundle".
        // selectBundle, and therefore this method, is never reached.
        if (this.primaryCta) {
          this.primaryCta.disabled = false;
          this.primaryCta.removeAttribute('aria-disabled');
        }
        // Same snapshot problem, one input along. The hidden id is rendered
        // disabled by the same Liquid condition, and a disabled input is left
        // out of FormData entirely, so the add would post no variant at all.
        // product-form.js clears it in its own constructor; this covers the
        // case where that element never constructed.
        if (this.variantInput) this.variantInput.disabled = false;
      }

      /**
       * Floored, never rounded. This number is an advertised claim, and
       * rounding up produces a percentage the receipt cannot back: 40.6% off
       * shown as 41% overstates the saving by a few pence on every order.
       */
      discountPercent(originalPrice, discountedPrice) {
        if (originalPrice <= 0 || discountedPrice >= originalPrice) return 0;
        return Math.floor(((originalPrice - discountedPrice) / originalPrice) * 100);
      }

      /**
       * Mirrors snippets/h7-sale-price.liquid: the reference price is the one
       * the advertised percentage came off, so 20% off means price / 0.8,
       * charmed onto a .99 ending. Returns 0 when there is nothing to strike
       * through.
       */
      saleComparePrice(price) {
        if (!this.salePercent || !(price > 0)) return 0;
        const reference = Math.round((price * 100) / (100 - this.salePercent));
        if (reference <= price) return 0;
        const charmed = Math.round((reference + 1) / 100) * 100 - 1;
        return charmed > price ? charmed : reference;
      }

      renderAnchorPrice(element, price) {
        if (!element) return;
        const anchor = this.state?.regularTotal || 0;
        element.hidden = !(anchor > price);
        if (anchor > price) element.textContent = this.money(anchor);
      }

      renderSaleComparePrice(element, price) {
        if (!element) return;
        const reference = this.saleComparePrice(price);
        element.hidden = reference === 0;
        if (reference) element.textContent = this.money(reference);
      }

      subscriptionInterval(planName) {
        const normalized = String(planName || '').toLowerCase();
        const explicit = normalized.match(/(?:every|each)\s+(\d+)\s*months?/);
        if (explicit) return Number(explicit[1]);
        if (normalized.includes('month')) return 1;
        return 0;
      }

      subscriptionFrequencyLabel(planName) {
        const interval = this.subscriptionInterval(planName);
        // No readable interval means the plan is named in a way this cannot
        // parse, so the admin name is shown rather than a made-up cadence.
        if (!interval) return planName;
        return interval === 1
          ? this.frequencyMonthlyLabel
          : this.frequencyMonthsTemplate.replace('{count}', interval);
      }

      applySubscriptionPlan(plan) {
        if (!this.state || !plan) return;
        this.state.subscriptionPlanId = plan.id;
        this.state.subscriptionVariantId = plan.variantId || this.state.subscriptionVariantId;
        this.state.subscriptionTotal = plan.total;
        this.state.subscriptionRenewalTotal = plan.renewalTotal || plan.total;
        this.state.subscriptionName = plan.name;
      }

      syncSubscriptionFrequency() {
        if (!this.subscriptionFrequency || !this.subscriptionFrequencyWrap || !this.state) return;
        const plans = this.subscriptionPlans
          .filter((plan) => plan.pouches === this.state.pouches && plan.id)
          .sort((a, b) => this.subscriptionInterval(a.name) - this.subscriptionInterval(b.name));

        this.subscriptionFrequency.replaceChildren();
        this.subscriptionFrequencyWrap.hidden = plans.length === 0;
        if (plans.length === 0) return;

        plans.forEach((plan) => {
          const option = document.createElement('option');
          option.value = plan.id;
          option.textContent = this.subscriptionFrequencyLabel(plan.name);
          this.subscriptionFrequency.append(option);
        });

        const preferredInterval = Math.min(this.state.pouches, 4);
        const rememberedId = this.subscriptionPlanSelections.get(this.state.pouches);
        const selectedPlan = plans.find((plan) => plan.id === rememberedId)
          || plans.find((plan) => this.subscriptionInterval(plan.name) === preferredInterval)
          || plans[0];
        this.subscriptionFrequency.value = selectedPlan.id;
        this.subscriptionPlanSelections.set(this.state.pouches, selectedPlan.id);
        this.applySubscriptionPlan(selectedPlan);
      }

      bundleState(button) {
        const oneTimeTotal = Math.max(0, Number(button.dataset.h7BundleCartTotal || this.unitPrice * Number(button.dataset.h7BundlePouches || button.dataset.h7Bundle || 1)));
        const pouches = Math.max(1, Number(button.dataset.h7BundlePouches || button.dataset.h7Bundle || 1));
        const regularTotal = Math.max(0, Number(button.dataset.h7BundleRegularTotal || oneTimeTotal));
        return {
          button,
          quantity: Math.max(1, Number(button.dataset.h7BundleCartQuantity || button.dataset.h7Bundle || 1)),
          pouches,
          servings: Math.max(1, Number(button.dataset.h7BundleServings || 30)),
          variantId: button.dataset.h7BundleVariantId || this.baseVariantId,
          subscriptionVariantId: button.dataset.h7SubscriptionVariantId || this.baseVariantId,
          // The cart line quantity for the plan, which is the one-time quantity
          // whenever a rung does not state its own. It used to fall back to the
          // pouch count, which is a different number the moment a rung carries
          // its own variant: the line is one of that variant, not one per pouch.
          subscriptionQuantity: Math.max(1, Number(button.dataset.h7SubscriptionQuantity || button.dataset.h7BundleCartQuantity || 1)),
          oneTimeTotal,
          regularTotal: Math.max(oneTimeTotal, regularTotal),
          subscriptionPlanId: button.dataset.h7SubscriptionPlanId || '',
          subscriptionTotal: Math.max(0, Number(button.dataset.h7SubscriptionTotal || 0)),
          subscriptionRenewalTotal: Math.max(0, Number(button.dataset.h7SubscriptionRenewalTotal || 0)),
          subscriptionName: button.dataset.h7SubscriptionName || '',
        };
      }

      selectBundle(button, trackChange) {
        const retainedPurchaseOption = this.selectedPurchaseOption;
        this.state = this.bundleState(button);
        this.syncSubscriptionFrequency();
        this.revealPurchaseOptions();

        this.buttons.forEach((item) => {
          const selected = item === button;
          item.classList.toggle('is-selected', selected);
          if (item.tagName === 'INPUT') {
            // aria-pressed is not valid on a radio; checked is the state.
            item.checked = selected;
          } else {
            item.setAttribute('aria-pressed', String(selected));
          }
          item.closest('[data-h7-ladder-row]')?.classList.toggle('is-selected', selected);
        });

        if (this.quantityInput) {
          this.quantityInput.value = String(this.state.quantity);
          this.quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
        if (this.variantInput && this.state.variantId) this.variantInput.value = this.state.variantId;
        if (this.pouchCountInput) {
          this.pouchCountInput.value = `${this.state.pouches} ${this.state.pouches === 1 ? this.pouchSingular : this.pouchPlural}`;
        }

        const subscriptionAvailable = Boolean(
          this.state.subscriptionPlanId &&
            this.state.subscriptionTotal > 0
        );
        if (this.subscriptionButton) {
          this.subscriptionButton.disabled = !subscriptionAvailable;
          this.subscriptionButton.classList.toggle('is-unavailable', !subscriptionAvailable);
          this.subscriptionButton.dataset.h7SellingPlanId = this.state.subscriptionPlanId;
        }
        this.subscriptionOption?.classList.toggle('is-unavailable', !subscriptionAvailable);
        // Taken off the page rather than greyed out. A rung that cannot be
        // subscribed to showed a card reading "Unavailable", which is an
        // unanswerable dead end where the shopper is deciding how to buy. The
        // single pouch is one-time by design, so on that rung the question is
        // not "which of these two" but simply "add it or not".
        if (this.subscriptionOption) this.subscriptionOption.hidden = !subscriptionAvailable;
        // A pouch change keeps the purchase type the shopper already chose.
        // A subscription can only fall back to one-time when the new rung has
        // no sellable plan.
        this.selectedPurchaseOption =
          retainedPurchaseOption === 'subscription' && subscriptionAvailable
            ? 'subscription'
            : 'one-time';

        this.updateDisplay();
        if (trackChange) {
          this.announceSelection();
          h7Track('bundle_change', {
            pouch_count: this.state.pouches,
            night_count: this.state.servings,
            price: this.state.oneTimeTotal / 100,
          });
        }
      }

      /**
       * Confirm the current choice in the offer panel's live region.
       *
       * Only ever called with the same trackChange flag the analytics calls
       * use, so the first paint stays silent: announcing a selection nobody
       * made would be noise every page load. The sentence is assembled from
       * fragments the server already translated (the pouch words and the
       * purchase-option label) plus the formatted price, so there is no new
       * string to add to the catalogue and no risk of an untranslated
       * announcement in a non-English market.
       */
      announceSelection() {
        if (!this.statusRegion) return;
        const detail = this.lastDetail;
        if (!detail) return;
        const pouchWord = detail.pouches === 1 ? this.pouchSingular : this.pouchPlural;
        this.statusRegion.textContent = [
          `${detail.pouches} ${pouchWord}`,
          detail.purchaseOptionLabel,
          detail.formattedPrice,
        ]
          .filter(Boolean)
          .join(', ');
      }

      selectPurchaseOption(option, trackChange) {
        if (option === 'subscription' && !this.state?.subscriptionPlanId) return;
        this.selectedPurchaseOption = option === 'subscription' ? 'subscription' : 'one-time';
        this.updateDisplay();

        if (trackChange) {
          this.announceSelection();
          h7Track('purchase_option_selection', {
            purchase_option: this.selectedPurchaseOption,
            pouch_count: this.state?.pouches,
          });
        }
      }

      setIncludedGifts(isSubscription) {
        if (!this.includedGiftsInput) return;
        const oneTimeEbookClaimed = !isSubscription && Boolean(this.giftClaimInput?.checked);
        const value = isSubscription
          ? this.includedGiftsInput.dataset.h7IncludedSubscription
          : this.includedGiftsInput.dataset.h7IncludedOneTime;
        if (this.giftClaimInput) this.giftClaimInput.disabled = isSubscription;
        this.includedGiftsInput.disabled = !isSubscription && !oneTimeEbookClaimed;
        if (value) this.includedGiftsInput.value = value;
      }

      updateSubscriberGifts(state) {
        if (!this.subscriberGifts) return;
        const copy = {
          teaser: {
            title: this.subscriberGifts.dataset.h7GiftTeaserTitle || 'Build your complete sleep kit',
            detail: this.subscriberGifts.dataset.h7GiftTeaserDetail || 'One complimentary gift arrives with each consecutive subscription delivery.',
          },
          locked: {
            title: this.subscriberGifts.dataset.h7GiftLockedTitle || 'Four gifts, reserved for subscribers',
            detail: this.subscriberGifts.dataset.h7GiftLockedDetail || 'Choose subscription to receive one gift with each consecutive delivery.',
          },
          unlocked: {
            title: this.subscriberGifts.dataset.h7GiftUnlockedTitle || 'Your four-delivery gift ladder',
            detail: this.subscriberGifts.dataset.h7GiftUnlockedDetail || 'One complimentary gift arrives with each consecutive delivery.',
          },
        }[state];
        if (!copy) return;
        this.subscriberGifts.dataset.h7GiftState = state;
        if (this.giftLadder) this.giftLadder.setAttribute('aria-disabled', String(state !== 'unlocked'));
        this.subscriberGifts.classList.toggle('is-teaser', state === 'teaser');
        this.subscriberGifts.classList.toggle('is-locked', state === 'locked');
        this.subscriberGifts.classList.toggle('is-unlocked', state === 'unlocked');
        if (this.giftStatusTitle) this.giftStatusTitle.textContent = copy.title;
        if (this.giftStatusDetail) this.giftStatusDetail.textContent = copy.detail;
      }

      updateDisplay() {
        if (!this.state) return;
        const isSubscription = this.selectedPurchaseOption === 'subscription' && this.state.subscriptionPlanId;
        const frequencyLabel = this.subscriptionFrequencyLabel(this.state.subscriptionName);
        const selectedQuantity = isSubscription ? this.state.subscriptionQuantity : this.state.quantity;
        const selectedVariantId = isSubscription ? this.state.subscriptionVariantId : this.state.variantId;
        const selectedTotal = isSubscription ? this.state.subscriptionTotal : this.state.oneTimeTotal;
        this.buttons.forEach((button) => {
          const amount = button.closest('[data-h7-ladder-row]')?.querySelector('[data-h7-ladder-night-amount]');
          if (!amount) return;
          const pouches = Number(button.dataset.h7BundlePouches);
          const servings = Math.max(1, Number(button.dataset.h7BundleServings) || 1);
          const savedPlan = this.subscriptionPlans.find((plan) =>
            plan.pouches === pouches && plan.id === this.subscriptionPlanSelections.get(pouches));
          const planTotal = pouches === this.state.pouches
            ? this.state.subscriptionTotal
            : savedPlan?.total || Number(button.dataset.h7SubscriptionTotal);
          const total = isSubscription && planTotal > 0
            ? planTotal
            : Number(button.dataset.h7BundleCartTotal);
          amount.textContent = this.money(total / servings);
        });
        // Measured against the struck reference sitting next to it, not against
        // the one-time price. renderAnchorPrice strikes state.regularTotal in
        // both rows, so the badge and the strike now describe the same drop.
        // Gated on there being a plan price at all. A rung with no plan has a
        // subscription total of 0, and measuring the reference against nothing
        // reads as the whole price saved: the single pouch card was painting a
        // "100% off" badge beside the word "Unavailable".
        const bundleSaving = Math.max(0, this.state.regularTotal - this.state.oneTimeTotal);
        const bundleDiscountPercent = this.discountPercent(this.state.regularTotal, this.state.oneTimeTotal);
        const hasPlanPrice = this.state.subscriptionTotal > 0;
        const subscriptionAnchorSaving = hasPlanPrice
          ? Math.max(0, this.state.regularTotal - this.state.subscriptionTotal)
          : 0;
        const subscriptionDiscountPercent = hasPlanPrice
          ? this.discountPercent(this.state.regularTotal, this.state.subscriptionTotal)
          : 0;
        const discountSuffix = this.dataset.discountSuffix || 'off';
        const ctaActionLabel = isSubscription
          ? this.dataset.subscribeLabel || window.variantStrings?.addToCart || 'Subscribe and Save'
          : this.dataset.oneTimeLabel || window.variantStrings?.addToCart || 'Add to cart';
        const formattedPrice = this.money(selectedTotal);
        const ctaLabel = `${ctaActionLabel} - ${formattedPrice}`;

        this.purchaseButtons.forEach((button) => {
          const selected = button.dataset.h7PurchaseOption === this.selectedPurchaseOption;
          button.classList.toggle('is-selected', selected);
          button.setAttribute('aria-pressed', String(selected));
        });
        this.subscriptionOption?.classList.toggle('is-selected', Boolean(isSubscription));
        if (this.sellingPlanInput) {
          this.sellingPlanInput.value = isSubscription ? this.state.subscriptionPlanId : '';
          this.sellingPlanInput.disabled = !isSubscription;
        }
        if (this.subscriptionPolicyInput) this.subscriptionPolicyInput.disabled = !isSubscription;
        this.setIncludedGifts(Boolean(isSubscription));
        if (this.checkoutGiftValueInput) this.checkoutGiftValueInput.disabled = !isSubscription;
        this.updateSubscriberGifts(isSubscription ? 'unlocked' : 'locked');
        if (this.subscriptionFrequencyWrap) {
          this.subscriptionFrequencyWrap.hidden = !this.subscriptionFrequency?.options.length;
        }
        if (this.quantityInput) this.quantityInput.value = String(selectedQuantity);
        if (this.variantInput && selectedVariantId) this.variantInput.value = selectedVariantId;
        if (this.total) this.total.textContent = this.money(selectedTotal);
        if (this.regularTotal) {
          const showRegular = this.state.regularTotal > selectedTotal;
          this.regularTotal.hidden = !showRegular;
          this.regularTotal.textContent = this.money(this.state.regularTotal);
        }
        // The literal is the one string in this file with no catalogue entry
        // and no data attribute behind it. It stays that way on purpose: no
        // section renders [data-h7-saving], so this branch is reachable only
        // from the test fixture, and giving it a translated label would put a
        // string into the catalogue that no shopper can ever read.
        if (this.saving) this.saving.textContent = bundleSaving > 0 ? `${bundleDiscountPercent}% ${discountSuffix}` : 'No bundle saving';
        if (this.perServing) this.perServing.textContent = `${this.money(selectedTotal / this.state.servings)} ${this.perServingLabel}`;
        if (this.perPouch) {
          this.perPouch.hidden = this.state.pouches === 1;
          this.perPouch.textContent = `${this.money(selectedTotal / this.state.pouches)} ${this.perPouchLabel}`;
        }
        // The strike on each buy choice is the selected rung's list price, the
        // figure the ladder is sold under, rather than something derived from
        // the sale percentage. Both options strike the same anchor because it
        // is the list price of that quantity, whichever way it is bought.
        this.renderAnchorPrice(this.oneTimeComparePrice, this.state.oneTimeTotal);
        if (this.oneTimeOptionPrice) this.oneTimeOptionPrice.textContent = this.money(this.state.oneTimeTotal);
        this.renderAnchorPrice(this.subscriptionComparePrice, this.state.subscriptionTotal);
        if (this.subscriptionOptionPrice) {
          this.subscriptionOptionPrice.textContent = this.state.subscriptionTotal
            ? this.money(this.state.subscriptionTotal)
            : this.subscriptionUnavailableLabel;
        }
        if (this.oneTimeHeading) {
          this.oneTimeHeading.textContent = this.state.pouches === 4
            ? this.fourMonthSupplyLabel
            : this.oneTimeHeadingLabel;
        }
        if (this.subscriptionHeading) {
          this.subscriptionHeading.textContent = this.state.pouches === 4
            ? this.fourMonthClubLabel
            : this.subscriptionHeadingLabel;
        }
        if (this.subscriptionContents) {
          // Pouches, not the cart line quantity. This line promises what turns
          // up in the box, and a rung with its own variant submits one line
          // holding all of them, so the quantity is 1 on every rung and would
          // have every plan promising a single pouch. First paint renders the
          // same count from h7_default_pouches, so the two agree.
          const contentsCount = this.state.pouches;
          // Always the digit. The spelled-out sentences this used to prefer
          // only existed for one, two and four, so the three-pouch rung read
          // "3 pouches" beside neighbours saying "One" and "Two".
          this.subscriptionContents.textContent = this.subscriptionContentsTemplate
            .replace('{count}', contentsCount)
            .replace('{pouch}', contentsCount === 1 ? this.pouchSingular : this.pouchPlural);
        }
        if (this.subscriptionOptionDetail) {
          this.subscriptionOptionDetail.textContent = frequencyLabel || this.subscriptionPendingLabel;
        }
        if (this.subscriptionSavingLabel) {
          // Gated on the same comparison it prints. Gating on the one-time
          // saving instead would blank a real discount whenever a plan happened
          // to match the one-time price while the reference sat above both.
          this.subscriptionSavingLabel.hidden = subscriptionAnchorSaving <= 0;
          this.subscriptionSavingLabel.textContent = subscriptionAnchorSaving > 0
            ? `${subscriptionDiscountPercent}% ${discountSuffix}`
            : '';
        }
        if (this.savingInput) {
          // This value travels to the cart and the checkout, so it has to be
          // the same saving the cart derives: the sale reference for the
          // undiscounted total, less what is actually charged.
          const referenceTotal = Math.max(
            this.saleComparePrice(this.state.regularTotal) || 0,
            this.state.regularTotal,
            selectedTotal
          );
          this.savingInput.value = this.money(Math.max(0, referenceTotal - selectedTotal));
        }
        if (this.primaryCtaText && ctaLabel) this.primaryCtaText.textContent = ctaLabel;

        const detail = {
          pouches: this.state.pouches,
          servings: this.state.servings,
          purchaseOption: this.selectedPurchaseOption,
          purchaseOptionLabel: isSubscription ? this.subscriptionOptionLabel : this.oneTimeOptionLabel,
          price: selectedTotal,
          originalPrice: this.state.regularTotal,
          actionLabel: ctaActionLabel,
          formattedPrice,
          salePrice: this.saleComparePrice(selectedTotal),
          formattedSalePrice: this.saleComparePrice(selectedTotal)
            ? this.money(this.saleComparePrice(selectedTotal))
            : '',
          label: ctaLabel,
        };
        this.lastDetail = detail;
        this.dispatchEvent(new CustomEvent('h7:offer-change', { bubbles: true, detail }));
      }
    }
  );
}

if (!customElements.get('h7-purchase-sticky')) {
  customElements.define(
    'h7-purchase-sticky',
    class H7PurchaseSticky extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;
        this.optionLabels = Array.from(this.querySelectorAll('[data-h7-sticky-option]'));
        this.priceLabels = Array.from(this.querySelectorAll('[data-h7-sticky-price]'));
        this.comparePriceLabels = Array.from(this.querySelectorAll('[data-h7-sticky-compare-price]'));
        this.buttons = Array.from(this.querySelectorAll('[data-h7-sticky-submit]'));
        this.pouchSelects = Array.from(this.querySelectorAll('[data-h7-sticky-pouches]'));
        this.footer =
          document.querySelector('.h7-footer') ||
          document.querySelector('footer.footer') ||
          document.querySelector('body > footer');
        this.classList.add('is-awaiting-bundle');
        this.buttons.forEach((button) => (button.disabled = true));

        this.updateFooterClearance = () => {
          this.footerFrame = null;
          if (!this.footer || !this.classList.contains('is-visible')) {
            this.style.setProperty('--h7-sticky-footer-lift', '0px');
            return;
          }

          const desktopBottom = window.matchMedia('(max-width: 899px)').matches ? 0 : 20;
          const footerGap = 16;
          const footerTop = this.footer.getBoundingClientRect().top;
          const lift = Math.max(0, Math.ceil(window.innerHeight - desktopBottom - footerTop + footerGap));
          this.style.setProperty('--h7-sticky-footer-lift', `${lift}px`);
        };
        this.queueFooterClearance = () => {
          if (this.footerFrame) return;
          this.footerFrame = window.requestAnimationFrame(this.updateFooterClearance);
        };
        window.addEventListener('scroll', this.queueFooterClearance, { passive: true });
        window.addEventListener('resize', this.queueFooterClearance);

        if (this.footer && 'ResizeObserver' in window) {
          this.footerResizeObserver = new ResizeObserver(this.queueFooterClearance);
          this.footerResizeObserver.observe(this.footer);
        }

        this.pouchSelects.forEach((select) => {
          select.addEventListener('change', () => {
            const offer = this.currentOffer();
            const bundle = offer?.buttons?.find((button) => button.dataset.h7BundlePouches === select.value)
              || offer?.querySelector(`[data-h7-bundle-pouches="${select.value}"]`);
            if (bundle) offer.selectBundle(bundle, true);
          });
        });
        this.buttons.forEach((button) => {
          button.addEventListener('click', () => {
            h7Track('sticky_purchase_click', { location: window.innerWidth < 750 ? 'mobile' : 'desktop' });
            this.currentPrimaryCta()?.click();
          });
        });
        // Kept on `this` so disconnectedCallback can remove it; an anonymous
        // handler accumulated once per section re-render and kept the whole
        // detached subtree alive while writing into it.
        this.onOfferChange = (event) => this.update(event.detail);
        document.addEventListener('h7:offer-change', this.onOfferChange);
        const openingDetail = this.currentOffer()?.lastDetail;
        if (openingDetail) this.update(openingDetail);

        // The bar belongs to the buy box: it stands in for the Add to cart
        // button only once that button has scrolled off the top of the screen.
        //
        // This is a scroll question, not an intersection one, and an
        // IntersectionObserver could not answer it. The observer only fires
        // when the target crosses the viewport edge, so any jump that takes the
        // buy box from below the fold to above it without ever touching the
        // screen left the bar on whatever state it was last in. The hero button
        // is an anchor to #h7-shop, and a flick on a phone does the same thing:
        // scroll down past the buy box and the bar came on, jump back to the
        // top and it stayed on, sitting over the hero with the real CTA a
        // screen and a half below. Reading the rect on each frame is always
        // right regardless of how the page got there.
        //
        // Two conditions, not one. The buy box CTA has to be above the top of
        // the screen, and the hero has to be off it: the bar stands in for the
        // buy box, and the hero already carries a full-width CTA of its own, so
        // one over the other is two competing primary actions and a bar bleeding
        // across the guarantee line. The hero test is what makes the reported
        // case impossible rather than merely unlikely.
        this.hero = document.querySelector('.h7-ryze-hero__stage');
        this.syncVisibility = () => {
          this.visibilityFrame = null;
          const primaryCta = this.currentPrimaryCta();
          if (!primaryCta) return;
          const clearedCta = primaryCta.getBoundingClientRect().bottom <= 0;
          const clearedHero = !this.hero || this.hero.getBoundingClientRect().bottom <= 0;
          const visible = clearedCta && clearedHero;
          if (visible === this.classList.contains('is-visible')) return;
          this.classList.toggle('is-visible', visible);
          this.setAttribute('aria-hidden', String(!visible));
          // inert moves with aria-hidden. Without it the bar's button and
          // pouch select stay in the tab order while the bar is off-screen and
          // declared hidden, which is a focusable control inside an
          // aria-hidden subtree: the screen reader is told there is nothing
          // there and the keyboard lands on it anyway.
          this.toggleAttribute('inert', !visible);
          this.queueFooterClearance();
        };
        this.queueVisibility = () => {
          if (this.visibilityFrame) return;
          this.visibilityFrame = window.requestAnimationFrame(this.syncVisibility);
        };
        window.addEventListener('scroll', this.queueVisibility, { passive: true });
        window.addEventListener('resize', this.queueVisibility);
        // pageshow covers a back-forward restore, which lands at a saved offset
        // without firing a scroll event at all.
        window.addEventListener('pageshow', this.queueVisibility);
        this.syncVisibility();
      }

      disconnectedCallback() {
        this.footerResizeObserver?.disconnect();
        if (this.onOfferChange) document.removeEventListener('h7:offer-change', this.onOfferChange);
        window.removeEventListener('scroll', this.queueFooterClearance);
        window.removeEventListener('resize', this.queueFooterClearance);
        window.removeEventListener('scroll', this.queueVisibility);
        window.removeEventListener('resize', this.queueVisibility);
        window.removeEventListener('pageshow', this.queueVisibility);
        if (this.footerFrame) window.cancelAnimationFrame(this.footerFrame);
        if (this.visibilityFrame) window.cancelAnimationFrame(this.visibilityFrame);
        // Everything above was torn down, so connectedCallback must be allowed
        // to rebuild it if the editor re-attaches the element.
        this.initialized = false;
      }

      /*
       * The buy box and the bar are separate elements, and product-info
       * replaces the whole buy box when it re-renders. Holding either of these
       * from connectedCallback left the bar driving a detached buy box: the
       * pouch select changed nothing, the sticky button clicked a button that
       * was no longer on the page, and syncVisibility measured a detached rect,
       * which reads as all zeros and pinned the bar on over the hero.
       *
       * Cached rather than re-queried per call, because syncVisibility runs on
       * every scroll frame; isConnected is the cheap test that says when the
       * cache is stale.
       */
      currentOffer() {
        if (!this.offer?.isConnected) this.offer = document.querySelector('h7-product-offer');
        return this.offer;
      }

      currentPrimaryCta() {
        if (!this.primaryCta?.isConnected) this.primaryCta = document.querySelector('[data-h7-primary-cta]');
        return this.primaryCta;
      }

      update(detail) {
        this.classList.remove('is-awaiting-bundle');
        this.buttons.forEach((button) => (button.disabled = false));
        const offer = this.currentOffer();
        const pouchLabel = detail.pouches === 1 ? offer?.pouchSingular || 'pouch' : offer?.pouchPlural || 'pouches';
        const option = detail.purchaseOptionLabel || (detail.purchaseOption === 'subscription' ? 'subscribe & save' : 'one-time purchase');
        const label = `${detail.pouches} ${pouchLabel} · ${option}`;
        this.optionLabels.forEach((element) => (element.textContent = label));
        this.priceLabels.forEach((element) => (element.textContent = detail.formattedPrice));
        this.comparePriceLabels.forEach((element) => {
          element.hidden = !detail.formattedSalePrice;
          if (detail.formattedSalePrice) element.textContent = detail.formattedSalePrice;
        });
        this.pouchSelects.forEach((element) => (element.value = String(detail.pouches)));
        this.buttons.forEach((element) => (element.textContent = detail.actionLabel));
      }
    }
  );
}

/*
 * Subscriber gift pack carousel.
 *
 * Desktop shows five tiles and scrolls the rest, so the pack can take new
 * gifts without the row shrinking. The arrows only exist while the track
 * actually overflows: on mobile the list wraps into a grid, the track never
 * scrolls, and both buttons stay hidden. Bound on the document rather than per
 * element because product-info replaces the whole buy box when the variant
 * re-renders, which would drop element-level listeners.
 */
const h7GiftCarouselSync = (carousel) => {
  if (!carousel) return;
  const track = carousel.querySelector('[data-h7-gift-track]');
  const prev = carousel.querySelector('[data-h7-gift-scroll="-1"]');
  const next = carousel.querySelector('[data-h7-gift-scroll="1"]');
  if (!track || !prev || !next) return;
  // A one pixel slack: fractional widths leave scrollLeft a hair short of the
  // maximum even when the track is scrolled all the way to the end.
  const overflow = track.scrollWidth - track.clientWidth;
  const scrollable = overflow > 1;
  prev.hidden = !scrollable;
  next.hidden = !scrollable;
  if (!scrollable) return;
  prev.disabled = track.scrollLeft <= 1;
  next.disabled = track.scrollLeft >= overflow - 1;
};

const h7SyncGiftCarousels = () => {
  document.querySelectorAll('[data-h7-gift-carousel]').forEach(h7GiftCarouselSync);
};

// The editor re-runs this file on every section re-render, so the document
// listeners are wired once and only the sync is repeated.
if (!window.h7GiftCarouselBound) {
  window.h7GiftCarouselBound = true;

  document.addEventListener('click', (event) => {
    const button = event.target.closest?.('[data-h7-gift-scroll]');
    if (!button) return;
    const carousel = button.closest('[data-h7-gift-carousel]');
    const track = carousel?.querySelector('[data-h7-gift-track]');
    if (!track) return;
    const direction = Number(button.dataset.h7GiftScroll) || 1;
    const tile = track.querySelector('li');
    // One tile plus its gap, so a press lands the next tile flush against the
    // left edge rather than mid-tile.
    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    const step = tile ? tile.getBoundingClientRect().width + gap : track.clientWidth;
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    const from = track.scrollLeft;
    const target = Math.max(0, Math.min(from + step * direction, track.scrollWidth - track.clientWidth));
    if (reduced) {
      track.scrollLeft = target;
      return;
    }
    track.scrollTo({ left: target, behavior: 'smooth' });
    // Some engines drop a smooth scroll on the floor: the animation never
    // starts and the press does nothing. If nothing has moved by the time the
    // animation should be well under way, the jump is taken outright, which is
    // still better than an arrow that looks broken.
    window.setTimeout(() => {
      if (track.scrollLeft === from && from !== target) track.scrollLeft = target;
    }, 350);
  });

  document.addEventListener(
    'scroll',
    (event) => {
      const track = event.target?.closest?.('[data-h7-gift-track]');
      if (track) h7GiftCarouselSync(track.closest('[data-h7-gift-carousel]'));
    },
    true
  );

  window.addEventListener('resize', h7SyncGiftCarousels);
  // The tiles carry images, so the track's scrollWidth is not final until they
  // have laid out.
  window.addEventListener('load', h7SyncGiftCarousels);
  // A variant re-render swaps the whole panel in, arrows included.
  document.addEventListener('h7:offer-change', h7SyncGiftCarousels);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', h7SyncGiftCarousels, { once: true });
} else {
  h7SyncGiftCarousels();
}
})();
